import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Lock, FileText, Database, UserCheck, Trash2, Mail, Phone, ExternalLink, CheckCircle } from 'lucide-react';

interface PrivacyPolicyPageProps {
  onBack?: () => void;
}

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Kebijakan Privasi — LetonKu';
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      try {
        window.history.pushState(null, '', '/#home');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch {
        window.location.href = '/#home';
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans antialiased selection:bg-[#0056D2] selection:text-white">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-2xs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-700 hover:text-[#0056D2] font-semibold text-xs sm:text-sm px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span>Kembali ke Beranda</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#0056D2] text-white flex items-center justify-center font-bold text-xs shadow-xs">
              LK
            </div>
            <span className="font-bold text-sm sm:text-base tracking-tight text-slate-900">
              LetonKu
            </span>
          </div>
        </div>
      </header>

      {/* Hero Banner Section */}
      <div className="bg-gradient-to-b from-white via-slate-50 to-[#F8FAFC] border-b border-slate-200 py-10 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-[#0056D2] text-xs font-bold tracking-wide uppercase shadow-2xs">
            <Shield className="w-3.5 h-3.5" />
            <span>Kepatuhan &amp; Transparansi Data</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Kebijakan Privasi LetonKu
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Dokumen resmi ini menjelaskan bagaimana aplikasi <strong>LetonKu</strong> mengumpulkan, menggunakan, menyimpan, melindungi, dan menghapus data pribadi pengguna sesuai standar perlindungan data pribadi dan ketentuan Google Play Console.
          </p>
          <div className="pt-2 text-[11px] text-slate-400 font-mono">
            Terakhir diperbarui: 29 Oktober 2026 • Versi Dokumen: 1.0.0
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8">
        
        {/* Ringkasan Singkat Box */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>Ringkasan Komitmen Privasi LetonKu</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">Data Minimum</span>
              <p className="text-[11px] text-slate-600 leading-normal">
                Hanya mengumpulkan data yang esensial untuk pemesanan kopi, pengantaran, dan program loyalitas.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">Tanpa Penjualan Data</span>
              <p className="text-[11px] text-slate-600 leading-normal">
                LetonKu tidak pernah dan tidak akan menjual data pribadi Anda kepada pihak ketiga atau pengiklan.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">Hak Hapus Akun</span>
              <p className="text-[11px] text-slate-600 leading-normal">
                Anda dapat meminta penghapusan akun dan seluruh data terkait kapan saja secara mudah.
              </p>
            </div>
          </div>
        </section>

        {/* 1. Pendahuluan */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              1
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Pendahuluan
            </h2>
          </div>
          <p>
            Selamat datang di aplikasi <strong>LetonKu</strong> ("Aplikasi", "Layanan", atau "Kami"). LetonKu adalah platform pemesanan menu makanan &amp; minuman kopi spesialti serta program keanggotaan loyalty yang beroperasi di wilayah Dumai, Riau, Indonesia.
          </p>
          <p>
            Kebijakan Privasi ini mengatur pengumpulan, pemrosesan, dan perlindungan informasi pribadi Anda saat menggunakan aplikasi mobile, website pemesanan, atau fitur terkait dari LetonKu. Dengan mendaftar, mengakses, atau menggunakan aplikasi LetonKu, Anda menyatakan telah membaca dan memahami praktik yang diatur dalam dokumen ini.
          </p>
        </section>

        {/* 2. Data Pribadi yang Kami Kumpulkan */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              2
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Data Pribadi yang Kami Kumpulkan
            </h2>
          </div>
          <p>
            Dalam pengoperasian layanan pemesanan dan keanggotaan, kami mengumpulkan kategori data berikut:
          </p>
          <div className="space-y-3 pt-2">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-[#0056D2]" />
                <span>a. Informasi Akun &amp; Profil Pengguna</span>
              </h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs sm:text-sm ml-1">
                <li><strong>Nama Lengkap:</strong> Digunakan untuk identifikasi pesanan dan sapaan anggota.</li>
                <li><strong>Nomor Telepon / WhatsApp:</strong> Digunakan sebagai identitas login utama (single sign-on nomor HP), konfirmasi transaksi, serta pengiriman bukti status pesanan.</li>
                <li><strong>Kata Sandi Terenkripsi:</strong> Disimpan hanya dalam bentuk <em>cryptographic hash</em> (bcrypt) yang tidak dapat dibaca kembali oleh staf maupun sistem kami.</li>
                <li><strong>Avatar Profil:</strong> Aplikasi menggunakan karakter ilustrasi lokal bawaan. Kami tidak mewajibkan atau mengumpulkan foto wajah pribadi Anda.</li>
              </ul>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#0056D2]" />
                <span>b. Informasi Transaksi &amp; Pemesanan</span>
              </h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs sm:text-sm ml-1">
                <li>Rincian item menu, varian rasa, tingkat gula/es, catatan khusus pembuatan pesanan, dan nominal belanja.</li>
                <li>Pilihan outlet pengambilan/pengantaran (Chapter 5 Sudirman, Chapter 6 Kelakap 7, atau layanan mobile).</li>
                <li>Tipe pesanan: <em>Dine In</em> (nomor meja), <em>Take Away</em>, atau <em>Delivery</em> (alamat pengantaran, patokan, dan catatan kurir).</li>
                <li>Riwayat pesanan yang telah diselesaikan untuk pelacakan struk digital.</li>
              </ul>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#0056D2]" />
                <span>c. Program Loyalitas &amp; Referral</span>
              </h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs sm:text-sm ml-1">
                <li>Saldo <strong>Leton Points</strong>, riwayat perolehan poin dari transaksi valid, dan voucher reward yang ditukarkan.</li>
                <li>Status tingkatan membership (Silver, Gold, Platinum) dan progres pemenuhan syarat belanja.</li>
                <li>Kode referral unik akun untuk program reward teman.</li>
              </ul>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#0056D2]" />
                <span>d. Informasi Pembayaran</span>
              </h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs sm:text-sm ml-1">
                <li><strong>Metode QRIS:</strong> Gambar bukti transfer yang diunggah secara sukarela oleh pengguna untuk verifikasi kasir outlet.</li>
                <li><strong>Metode DANA (SNAP BI):</strong> Nomor referensi transaksi, status pembayaran, dan token otorisasi akun bertaut yang dienkripsi secara aman. <strong>LetonKu tidak pernah meminta, mengakses, atau menyimpan nomor PIN, kata sandi, atau data kartu perbankan Anda.</strong></li>
              </ul>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 flex items-center gap-2">
                <Database className="w-4 h-4 text-[#0056D2]" />
                <span>e. Notifikasi &amp; Data Perangkat</span>
              </h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs sm:text-sm ml-1">
                <li>Token langganan Web Push / Firebase Cloud Messaging (FCM) bila Anda menyetujui izin notifikasi perangkat, semata-mata untuk memberi tahu saat kopi Anda sedang diseduh atau siap diantar kurir.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 3. Tujuan Penggunaan Data */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              3
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Tujuan Penggunaan Data
            </h2>
          </div>
          <p>Data pribadi yang dikumpulkan digunakan secara eksklusif untuk tujuan operasional berikut:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-600">
            <li>Memproses, memverifikasi, meracik, dan menyerahkan/mengantarkan pesanan minuman dan makanan pelanggan.</li>
            <li>Mengelola saldo poin loyalitas, riwayat transaksi, dan hak voucher membership pelanggan.</li>
            <li>Mengirimkan notifikasi operasional terkait status pesanan pelanggan secara real-time via WhatsApp dan push notification.</li>
            <li>Memfasilitasi layanan pengantaran pesanan oleh kurir cabang ke alamat yang dituju.</li>
            <li>Menyediakan layanan bantuan pelanggan (Customer Care) apabila terdapat kendala pesanan atau pertanyaan akun.</li>
            <li>Mencegah penyalahgunaan akun, transaksi mencurigakan, dan menjaga integritas operasional.</li>
          </ul>
        </section>

        {/* 4. Penyimpanan, Lokasi, dan Keamanan Data */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              4
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Penyimpanan dan Keamanan Data
            </h2>
          </div>
          <p>
            Kami menerapkan standar pengamanan teknis dan organisasi yang ketat untuk melindungi data pribadi Anda dari akses, pengungkapan, perubahan, atau penghancuran tanpa hak:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-600">
            <li><strong>Enkripsi Jaringan:</strong> Seluruh komunikasi antara perangkat pengguna dan server dilindungi protokol enkripsi HTTPS/TLS berstandar industri.</li>
            <li><strong>Row Level Security (RLS):</strong> Basis data kami diamankan dengan aturan isolasi data tingkat baris (Row Level Security) sehingga pengguna hanya dapat mengakses data milik mereka sendiri.</li>
            <li><strong>Enkripsi Kredensial Pihak Ketiga:</strong> Token otentikasi DANA OAuth dienkripsi secara simetris di sisi server menggunakan algoritma AES-256-GCM.</li>
            <li><strong>Masa Penyimpanan (Retensi):</strong> Data akun dan riwayat pesanan disimpan selama akun pengguna aktif. Apabila pengguna meminta penghapusan akun, data profil akan dihapus permanen dalam kurun waktu 7 hari kerja, kecuali ringkasan transaksi finansial yang wajib disimpan untuk kepentingan pembukuan dan kepatuhan perpajakan yang diwajibkan oleh hukum yang berlaku di Indonesia.</li>
          </ul>
        </section>

        {/* 5. Pembagian Data kepada Pihak Ketiga */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              5
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Pembagian Data kepada Layanan Pihak Ketiga
            </h2>
          </div>
          <p>
            LetonKu <strong>tidak pernah menjual, menyewakan, atau memperdagangkan data pribadi pengguna kepada pihak ketiga atau pengiklan</strong>. Kami hanya membagikan data kepada mitra teknologi tepercaya yang secara langsung mendukung berjalannya fungsi aplikasi:
          </p>
          <div className="space-y-3 pt-1">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <strong className="text-slate-900 block text-xs sm:text-sm">1. Supabase (Database &amp; Cloud Storage)</strong>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5">
                Penyedia infrastruktur basis data cloud terenkripsi untuk menyimpan data akun pelanggan, katalog menu, dan riwayat pesanan.
              </p>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <strong className="text-slate-900 block text-xs sm:text-sm">2. DANA Indonesia (PT Espay Debit Indonesia Koe)</strong>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5">
                Gateway pembayaran resmi berstandar Bank Indonesia SNAP BI untuk memproses transaksi pembayaran digital DANA secara langsung.
              </p>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <strong className="text-slate-900 block text-xs sm:text-sm">3. WhatsApp (Meta Platforms, Inc.)</strong>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5">
                Platform pengiriman notifikasi instan dan komunikasi layanan pelanggan antara outlet dan nomor WhatsApp yang didaftarkan pengguna.
              </p>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <strong className="text-slate-900 block text-xs sm:text-sm">4. Firebase Cloud Messaging / Web Push</strong>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5">
                Layanan pengiriman pesan notifikasi status pesanan ke peramban atau perangkat mobile pengguna.
              </p>
            </div>
          </div>
        </section>

        {/* 6. Hak Pengguna & Prosedur Penghapusan Akun/Data (Google Play Requirement) */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-blue-100 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              6
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-rose-600" />
              <span>Hak Pengguna &amp; Prosedur Penghapusan Akun / Data Pribadi</span>
            </h2>
          </div>
          <p>
            Sesuai dengan ketentuan perlindungan data pribadi dan persyaratan transparansi Google Play Console, Anda memiliki hak penuh atas data pribadi Anda:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-slate-600">
            <li><strong>Hak Akses dan Perbaikan:</strong> Anda dapat memeriksa dan mengubah nama profil atau nomor kontak kapan saja melalui tab <em>Profil Akun</em> pada aplikasi.</li>
            <li><strong>Hak Penarikan Persetujuan:</strong> Anda dapat menonaktifkan izin notifikasi atau memutuskan tautan akun DANA kapan saja di menu pengaturan aplikasi.</li>
            <li><strong>Hak Penghapusan Akun &amp; Data Pribadi Secara Permanen:</strong> Anda berhak mengajukan permohonan agar akun, data login, riwayat alamat, dan identitas keanggotaan Anda dihapus secara menyeluruh dari sistem LetonKu.</li>
          </ul>

          <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 mt-3">
            <h3 className="font-bold text-slate-900 text-xs sm:text-sm">
              Cara Mengajukan Penghapusan Akun:
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-slate-600 text-xs sm:text-sm">
              <li>
                Hubungi tim layanan pelanggan resmi LetonKu melalui salah satu kanal berikut:
                <div className="pl-4 pt-1.5 space-y-1 font-mono text-[11px] sm:text-xs">
                  <div className="flex items-center gap-2 text-slate-800">
                    <Mail className="w-3.5 h-3.5 text-[#0056D2]" />
                    <span>Email: <strong>halo@letoncoffee.com</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-800">
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    <span>WhatsApp: <strong>+62 812-6860-0079</strong></span>
                  </div>
                </div>
              </li>
              <li>
                Cantumkan subjek <strong>"Permohonan Penghapusan Akun LetonKu"</strong> serta informasikan <strong>Nomor WhatsApp</strong> dan <strong>Nama Terdaftar</strong> yang ingin dihapus.
              </li>
              <li>
                Tim kami akan memverifikasi kepemilikan nomor WhatsApp tersebut untuk mencegah penghapusan akun oleh pihak yang tidak berwenang.
              </li>
              <li>
                Setelah verifikasi berhasil, akun Anda akan dinonaktifkan dan seluruh data pribadi terkait akan dihapus secara permanen dari basis data aktif kami dalam kurun waktu maksimal <strong>7 (tujuh) hari kerja</strong>.
              </li>
            </ol>
            <p className="text-[11px] text-slate-500 italic pt-1 border-t border-slate-200">
              *Catatan: Ringkasan catatan pembukuan transaksi agregat dapat diarsipkan tanpa data identitas pribadi untuk mematuhi kewajiban perpajakan dan hukum yang berlaku di Republik Indonesia.
            </p>
          </div>
        </section>

        {/* 7. Privasi Anak */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              7
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Privasi Anak-Anak (Children's Privacy)
            </h2>
          </div>
          <p>
            Layanan LetonKu tidak ditujukan secara khusus kepada individu yang berusia di bawah 13 tahun. Kami tidak secara sengaja mengumpulkan data identitas pribadi dari anak-anak di bawah umur. Apabila orang tua atau wali sah mengetahui bahwa anak mereka telah memberikan data pribadi kepada kami tanpa izin, harap hubungi kami agar kami dapat segera mengambil tindakan penghapusan data tersebut.
          </p>
        </section>

        {/* 8. Perubahan atas Kebijakan Privasi */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              8
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Pembaruan Kebijakan Privasi
            </h2>
          </div>
          <p>
            Kami dapat memperbarui dokumen Kebijakan Privasi ini dari waktu ke waktu guna menyesuaikan dengan pembaruan fitur aplikasi, perubahan hukum, atau standar Google Play Console. Setiap pembaruan akan dipublikasikan pada halaman ini dengan mencantumkan tanggal revisi terbaru di bagian atas. Kami menyarankan Anda untuk meninjau halaman ini secara berkala.
          </p>
        </section>

        {/* 9. Informasi Kontak & Alamat Pengelola */}
        <section className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0056D2] flex items-center justify-center font-bold text-xs">
              9
            </div>
            <h2 className="font-bold text-base sm:text-lg text-slate-900">
              Kontak Resmi Pengelola Layanan
            </h2>
          </div>
          <p>
            Apabila Anda memiliki pertanyaan, klarifikasi, atau keluhan terkait kebijakan privasi dan pengelolaan data pribadi di aplikasi LetonKu, silakan hubungi kami melalui:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block">Kanal Komunikasi</span>
              <div className="space-y-1.5 text-xs sm:text-sm text-slate-800">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#0056D2]" />
                  <span>Email: <strong>halo@letoncoffee.com</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-emerald-600" />
                  <span>WhatsApp: <strong>+62 812-6860-0079</strong></span>
                </div>
              </div>
              <p className="text-[10px] text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200 mt-2">
                Catatan Konfirmasi: Alamat email dan WhatsApp di atas diambil dari konfigurasi resmi aplikasi saat ini. Silakan sesuaikan jika terdapat email korporat khusus (misal: privacy@letonku.com).
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block">Lokasi Operasional Outlet</span>
              <div className="space-y-2 text-xs text-slate-600">
                <div>
                  <strong className="text-slate-800 block">Leton Chapter 5 (Sudirman):</strong>
                  <span>Jl. Jend. Sudirman No. 88, Dumai Kota, Riau, Indonesia.</span>
                </div>
                <div>
                  <strong className="text-slate-800 block">Leton Chapter 6 (Kelakap 7):</strong>
                  <span>Jl. Ratu Sima / Kelakap 7, Dumai Barat, Riau, Indonesia.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer Bottom Note */}
      <footer className="border-t border-slate-200 bg-white py-6 px-4 text-center text-xs text-slate-500">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© 2026 LetonKu. Seluruh hak cipta dilindungi.</p>
          <button
            type="button"
            onClick={handleBack}
            className="text-[#0056D2] font-semibold hover:underline cursor-pointer"
          >
            Kembali ke Halaman Utama LetonKu
          </button>
        </div>
      </footer>
    </div>
  );
};
